import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeProviderContext = createContext();

const getSystemTheme = () => (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
const resolveTheme = (theme) => (theme === "system" ? getSystemTheme() : theme);

export function ThemeProvider({ children, defaultTheme = "light", storageKey = "rideapp-theme" }) {
  const [theme, setTheme] = useState(() => localStorage.getItem(storageKey) || defaultTheme);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(localStorage.getItem(storageKey) || defaultTheme));

  useEffect(() => {
    const root = window.document.documentElement;
    const applyTheme = (resolved) => {
      root.classList.remove("light", "dark");
      root.classList.add(resolved);
      setResolvedTheme(resolved);
      document.querySelector("meta[name='theme-color']")?.setAttribute("content", `hsl(var(--background))`);
    };

    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      applyTheme(mediaQuery.matches ? "dark" : "light");
      const handleChange = (event) => applyTheme(event.matches ? "dark" : "light");
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    applyTheme(theme);
  }, [theme]);

  const handleSetTheme = useCallback(
    (newTheme) => {
      localStorage.setItem(storageKey, newTheme);
      setTheme(newTheme);
    },
    [storageKey],
  );

  const value = useMemo(() => ({ theme, resolvedTheme, setTheme: handleSetTheme }), [theme, resolvedTheme, handleSetTheme]);

  return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};
